import React, { Suspense, useEffect, lazy, useRef, useState } from 'react'
import { useFeatureFlags } from '@fs/zion-flags'
import { Switch, Route, AuthRoute, NotFound } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { NoticeLoading } from '@fs/zion-icon'
import { getSessionId, getAnonSession } from '@fs/zion-session'
import { identService } from '@fs/zion-memories/memories-authorized-use-only'
import { targetEnv } from '@fs/zion-config'
import { GalleryProvider } from './pages/Gallery/GalleryContext'
import QueryClientProviderWrapper from './QueryClientProviderWrapper'
import { useSubHeaderItems } from './hooks'

const Overview = lazy(() => import('./pages/Overview'))
const Gallery = lazy(() => import('./pages/Gallery'))
const TreeMemoriesPage = lazy(() => import('./pages/TreeMemories'))
const MemoryPage = lazy(() => import('./pages/Memory'))
const Print = lazy(() => import('./pages/Print'))
const LearnMore = lazy(() => import('./pages/LearnMore'))
const SocialRedirect = lazy(() => import('./pages/SocialRedirect'))
const Restore = lazy(() => import('./pages/Restore'))
const Admin = lazy(() => import('./pages/Admin'))
const AISearch = lazy(() => import('./pages/AISearch/AISearch'))
const Chatbot = lazy(() => import('./pages/AISearch/chatbot'))
const Feed = lazy(() => import('./pages/Feed'))
const Feedback = lazy(() => import('./pages/Feedback'))

function App({ setHideHeader, setHideFooter, setHideSubnav, setRootFullScreen, setHideChatbot, setSubHeaderItems }) {
  getAnonSession()
  const sessionId = getSessionId()
  const isCurrent = useRef(true)
  const [authorized, setAuthorized] = useState(false)
  const subHeaderItems = useSubHeaderItems({ showAdminContent: authorized })

  const {
    memories_dev: { isOn: showDevFeaturesFlag },
    memories_semanticSearchPOC: { isOn: showSemanticSearchPage },
    memories_eol_remove_peopleAndFind: { isOn: hidePeopleAndFind },
    memories_feed_migration: { isOn: showFamilyFeed },
  } = useFeatureFlags([
    'memories_dev',
    'memories_semanticSearchPOC',
    'memories_eol_remove_peopleAndFind',
    'memories_feed_migration',
  ])

  useEffect(() => {
    const getAdminPermissions = async () => {
      try {
        if (sessionId) {
          const response = await identService.getPermissions({
            context: 'ArtCtxOps',
            role: 'WebMemPrmAdmin',
            sessionId,
          })
          isCurrent.current && setAuthorized(response?.data?.authorized && ['int', 'beta', 'local'].includes(targetEnv))
        }
      } catch (error) {
        console.error(error)
      }
    }

    showDevFeaturesFlag && getAdminPermissions()
  }, [sessionId, showDevFeaturesFlag])

  useEffect(() => {
    setSubHeaderItems(subHeaderItems)
  }, [subHeaderItems, setSubHeaderItems])

  useEffect(() => {
    isCurrent.current = true
    return () => {
      isCurrent.current = false
    }
  }, [])

  return (
    <ErrorBoundary FallbackComponent={NotFound}>
      <Suspense fallback={<NoticeLoading />}>
        <Switch>
          <Route exact requirePersonalData path="/">
            <Overview />
          </Route>
          {/* The gallery route needs to not be an AuthRoute so the /gallery/album page is public accessible. */}
          <Route requirePersonalData path="/gallery">
            <GalleryProvider>
              <Gallery setHideFooter={setHideFooter} />
            </GalleryProvider>
          </Route>
          {!hidePeopleAndFind && (
            <AuthRoute requirePersonalData path="/people">
              <Feedback />
            </AuthRoute>
          )}
          {!hidePeopleAndFind && (
            <AuthRoute requirePersonalData path="/find">
              <Feedback />
            </AuthRoute>
          )}
          {showFamilyFeed && (
            <AuthRoute requirePersonalData path="/feed">
              <QueryClientProviderWrapper>
                <Feed />
              </QueryClientProviderWrapper>
            </AuthRoute>
          )}
          <Route path="/learnmore">
            <LearnMore />
          </Route>
          {showSemanticSearchPage && (
            <AuthRoute exact requirePersonalData path="/semantic-search">
              <AISearch />
            </AuthRoute>
          )}
          {showSemanticSearchPage && (
            <AuthRoute exact requirePersonalData path="/search-agent">
              <Chatbot />
            </AuthRoute>
          )}
          <Route requirePersonalData path="/socialRedirect">
            <SocialRedirect />
          </Route>
          <AuthRoute requirePersonalData path="/restore">
            <Restore />
          </AuthRoute>
          {/* These two "/memory" routes must remain in this order */}
          <Route requirePersonalData path="/memory/print/:id">
            <Print setHideHeader={setHideHeader} setHideFooter={setHideFooter} setHideChatbot={setHideChatbot} />
          </Route>
          <Route requirePersonalData path="/memory/:id/:slug?">
            <MemoryPage setHideSubnav={setHideSubnav} setRootFullScreen={setRootFullScreen} />
          </Route>
          {showDevFeaturesFlag && (
            <AuthRoute requirePersonalData path="/tree/:pid?">
              <TreeMemoriesPage />
            </AuthRoute>
          )}
          {showDevFeaturesFlag && authorized && (
            <AuthRoute requirePersonalData path="/admin">
              <Admin />
            </AuthRoute>
          )}
          <Route>
            <NotFound />
          </Route>
        </Switch>
      </Suspense>
    </ErrorBoundary>
  )
}
export default App
